import React, { useCallback } from "react";

import cn from "classnames";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import { FormattedMessage, useIntl } from "react-intl";

import AnyColor from "@mapmycustomers/shared/types/AnyColor";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";

import TextWithInfo from "@app/component/typography/TextWithInfo";

import styles from "../ActivityTypesCommon.module.scss";

import ActivityTypeItem from "./ActivityTypeItem";

interface Props {
  activeTypes: ActivityType[];
  inactiveTypes: ActivityType[];
  onActivate: (id: ActivityType["id"], active: boolean) => void;
  onChangeColor: (activityType: ActivityType, color: AnyColor | undefined) => void;
  onModifyActivityTypes: (types: ActivityType[]) => void;
  onRename: (activityType: ActivityType, newName: string) => void;
  onToggleCheckIn: (activityType: ActivityType, isCheckIn: boolean) => void;
}

const ActiveList: React.FC<Props> = ({
  activeTypes,
  inactiveTypes,
  onActivate,
  onChangeColor,
  onModifyActivityTypes,
  onRename,
  onToggleCheckIn,
}) => {
  const intl = useIntl();
  const handleDragEnd = useCallback(
    ({ destination, source }: DropResult) => {
      if (destination) {
        const result = Array.from(activeTypes);
        const [removed] = result.splice(source.index, 1);
        result.splice(destination.index, 0, removed);
        onModifyActivityTypes([
          ...result.map((activityType, index) => ({ ...activityType, displayOrder: index + 1 })),
          ...inactiveTypes,
        ] as ActivityType[]);
      }
    },
    [activeTypes, inactiveTypes, onModifyActivityTypes]
  );

  return (
    <div>
      <div className={styles.header}>
        <p className={styles.activityTypeListTitle}>
          <FormattedMessage
            id="settings.activityTypes.list.title"
            defaultMessage="Edit and reorder existing types"
            description="Label for activity type list on settings"
          />
        </p>
        <TextWithInfo
          className={styles.checkInToggle}
          info={intl.formatMessage({
            id: "settings.activityTypes.checkIn.info",
            defaultMessage:
              "When logging a Check-In from the mobile app, users will only be able to select from types that are switched “On” below. Users can still log standard activities (without location verification) with any enabled type.",
            description: "Check In info for activity types on settings activity types",
          })}
        >
          {intl.formatMessage({
            id: "settings.activityTypes.checkIn.title",
            defaultMessage: "Allow Check-Ins?",
            description: "Check-in title for activity types",
          })}
        </TextWithInfo>
        <p className={styles.actionsLabel}>
          {intl.formatMessage({
            id: "settings.activityTypes.action.title",
            defaultMessage: "Actions",
            description: "Action title for activity types",
          })}
        </p>
      </div>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="droppable">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {activeTypes.map((item, index) => (
                <Draggable draggableId={String(item.id)} index={index} key={item.id}>
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.draggableProps}>
                      <ActivityTypeItem
                        activityType={item}
                        className={cn(styles.activityType, styles.activityTypeDraggable)}
                        dragHandleProps={provided.dragHandleProps}
                        index={index}
                        key={item.id}
                        onActivate={onActivate}
                        onChangeColor={onChangeColor}
                        onRename={onRename}
                        onToggleCheckIn={onToggleCheckIn}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

export default ActiveList;
